import { gql, useMutation, useQuery } from '@apollo/client';
import React from 'react';
import FavoriteIcon from '../../components/FavoriteIcon';
import Tooltip from '../../components/Tooltip';
import FAVORITE_POSTS_QUERY from '../../graphql/favoritePosts';
import POST_SUMMARY_FIELDS_FRAGMENT from '../../graphql/fragments/PostSummaryFields';
import type {
  FavoritePostMutation,
  FavoritePostMutationVariables,
  PostFavoriteButtonQuery,
  PostFavoriteButtonQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import updateQueryList from '../../utils/graphql/updateQueryList';
import useFavoritePosts from '../useFavoritePosts';
import PostActionButton from './PostActionButton';

const POST_QUERY = gql`
  query PostFavoriteButton($id: ID!) {
    post(id: $id) {
      ...PostSummaryFields
    }
  }
  ${POST_SUMMARY_FIELDS_FRAGMENT}
`;

const FAVORITE_POST_MUTATION = gql`
  mutation favoritePost($id: ID!, $add: Boolean) {
    favoritePost(id: $id, add: $add) {
      ...PostSummaryFields
    }
  }
  ${POST_SUMMARY_FIELDS_FRAGMENT}
`;

interface PostFavoriteButtonProps {
  postId: string;
}

const PostFavoriteButton = ({ postId }: PostFavoriteButtonProps) => {
  const { data: postData } = useQuery<
    PostFavoriteButtonQuery,
    PostFavoriteButtonQueryVariables
  >(POST_QUERY, {
    // The post is only used for optimistic updates
    fetchPolicy: 'cache-only',
    variables: { id: postId },
  });

  const favoritePosts = useFavoritePosts();
  const isFavorite = favoritePosts.some((fp) => fp.id === postId);

  const [toggleFavorite] = useMutation<
    FavoritePostMutation,
    FavoritePostMutationVariables
  >(FAVORITE_POST_MUTATION, {
    variables: { id: postId, add: !isFavorite },
    update: updateQueryList(
      { query: FAVORITE_POSTS_QUERY },
      'currentSession.user.favoritePosts',
      'favoritePost',
      !isFavorite,
    ),
    optimisticResponse: postData?.post
      ? {
          __typename: 'RootMutationType',
          favoritePost: {
            ...postData?.post,
            __typename: 'Post',
          },
        }
      : undefined,
  });

  const tooltip = isFavorite ? 'Unfavorite' : 'Favorite';

  return (
    <Tooltip
      tooltip={tooltip}
      placement="left"
      trigger="hover"
      popOverPortal={document.body}
    >
      <PostActionButton onClick={toggleFavorite} aria-label={tooltip}>
        <FavoriteIcon active={isFavorite} size="large" />
      </PostActionButton>
    </Tooltip>
  );
};

export default PostFavoriteButton;
